// Multi-language translations for the app
export const translations = {
  en: {
    // Header
    appTitle: "JR Kids Learning Hub",
    
    // Navigation
    nav: {
      home: "Home",
      dashboard: "Dashboard",
      gameZone: "Game Zone"
    },
    
    // Common
    common: {
      start: "Start",
      continue: "Continue",
      back: "Back",
      next: "Next",
      previous: "Previous",
      close: "Close",
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      loading: "Loading...",
      tryAgain: "Try Again",
      backToHome: "Back to Home",
      awesome: "Awesome!",
      congratulations: "Congratulations!"
    },
    
    // Categories
    categories: {
      math: "Math",
      science: "Science",
      geography: "Geography",
      animals: "Animals",
      history: "History",
      space: "Space",
      english: "English",
      shapes: "Shapes",
      colors: "Colors",
      music: "Music",
      sports: "Sports",
      food: "Food",
      technology: "Technology",
      art: "Art",
      nature: "Nature"
    },
    
    // Category Descriptions
    categoryDesc: {
      math: "Numbers, counting, and basic arithmetic",
      science: "Explore the wonders of science",
      geography: "Learn about countries and places",
      animals: "Discover amazing creatures",
      history: "Journey through time",
      space: "Explore the universe",
      english: "Language and grammar",
      shapes: "Learn about geometric shapes",
      colors: "Discover the rainbow",
      music: "Rhythm and melodies",
      sports: "Games and athletics",
      food: "Nutrition and cooking",
      technology: "Computers and gadgets",
      art: "Creativity and expression",
      nature: "Plants and environment"
    },
    
    // Quiz
    quiz: {
      selectCategory: "Select a Category",
      question: "Question",
      of: "of",
      score: "Score",
      correct: "Correct",
      yourAnswer: "Your answer:",
      correctAnswer: "Correct answer:",
      reviewAnswers: "Review Your Answers",
      noAnswer: "No answer (Time up)",
      timeUp: "TIME'S UP!",
      quizEnded: "Quiz ended due to timeout",
      completeWithinTime: "Complete the quiz within time to earn XP!"
    },
    
    // Results
    results: {
      perfectScore: "Perfect Score! You're a genius! 🎉",
      excellent: "Excellent work! Keep it up! 🌟",
      goodJob: "Good job! Practice makes perfect! 👍",
      keepTrying: "Keep trying! You'll do better next time! 💪"
    },
    
    // Difficulty
    difficulty: {
      easy: "Easy",
      medium: "Medium",
      hard: "Hard",
      selectDifficulty: "Select Difficulty",
      ages46: "Ages 4-6",
      ages79: "Ages 7-9",
      ages10plus: "Ages 10+",
      simpleQuestions: "Simple questions for young learners",
      moderateChallenge: "Moderate challenge for growing minds",
      advancedQuestions: "Advanced questions for experts"
    },
    
    // Quiz Modes
    modes: {
      practice: "Practice Mode",
      timed: "Timed Mode",
      practiceDesc: "Take your time, no pressure!",
      timedDesc: "Race against the clock!",
      selectMode: "Select Quiz Mode"
    },
    
    // Dashboard
    dashboard: {
      title: "Learning Dashboard",
      totalQuizzes: "Total Quizzes",
      averageScore: "Average Score",
      badgesEarned: "Badges Earned",
      bestCategory: "Best Category",
      categoryPerformance: "Category Performance",
      quizzesCompleted: "Quizzes Completed",
      bestScore: "Best Score",
      averageScoreLabel: "Average Score",
      lastPlayed: "Last Played",
      recentActivity: "Recent Activity",
      downloadReport: "Download Progress Report"
    },
    
    // Streak
    streak: {
      dayStreak: "Day Streak",
      longestStreak: "Longest Streak",
      totalDays: "Total Days",
      thisMonth: "This Month",
      freezeAvailable: "Freeze Available",
      streakFreeze: "Streak Freeze: Miss 1 day without breaking your streak!",
      viewCalendar: "View Activity Calendar",
      keepGoing: "Keep going tomorrow!",
      greatStart: "Great Start! 🎯"
    },
    
    // Gamification
    gamification: {
      level: "Level",
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
      expert: "Expert",
      grandMaster: "Grand Master",
      legendaryMaster: "Legendary Master",
      experiencePoints: "Experience Points",
      totalXP: "Total XP",
      xpToLevel: "XP to Level",
      viewLeaderboard: "View Leaderboard",
      dailyChallenges: "Daily Challenges",
      complete: "Complete",
      levelUp: "LEVEL {level}!",
      youveReached: "You've reached {title}!"
    },
    
    // Game Zone
    gameZone: {
      title: "Game Zone",
      subtitle: "Track your progress, complete challenges, and climb the leaderboard!",
      selectGame: "Select a Game",
      memoryMatch: "Memory Match",
      wordScramble: "Word Scramble",
      mathPuzzle: "Math Puzzle",
      memoryDesc: "Find matching pairs!",
      wordDesc: "Unscramble the words!",
      mathDesc: "Solve math problems!",
      moves: "Moves",
      time: "Time",
      youWon: "You Won!",
      gameComplete: "Game Complete!",
      playNow: "Play Now",
      miniGames: "Mini-Games",
      levelProgress: "Your Level & Progress",
      quickStats: "Quick Stats",
      daysPlayed: "Days Played",
      learningStreak: "Learning Streak",
      competition: "Competition",
      leaderboard: "Leaderboard",
      leaderboardDesc: "See how you rank against other players!",
      howToEarnXP: "How to Earn More XP",
      tip1: "✅ Complete quizzes on Hard difficulty for 2x XP",
      tip2: "⏱️ Play in Timed Mode for bonus points",
      tip3: "💯 Get perfect scores for extra XP",
      tip4: "🔥 Maintain your daily streak for streak XP",
      tip5: "🎯 Complete daily challenges for 50-100 XP each",
      tip6: "🏆 Earn badges for 30 XP each"
    },
    
    // Profile
    profile: {
      selectProfile: "Select Your Profile",
      createProfile: "Create New Profile",
      manageProfiles: "Manage Profiles",
      profileName: "Profile Name",
      age: "Age",
      selectAvatar: "Select Avatar",
      selectColor: "Select Color",
      editProfile: "Edit Profile",
      deleteProfile: "Delete Profile",
      confirmDelete: "Are you sure you want to delete this profile?",
      logout: "Logout"
    },
    
    // Badges
    badges: {
      earnedBadges: "Earned Badges",
      recommendations: "Recommendations",
      focusArea: "Focus Area: Consider spending more time on {category} to improve overall performance.",
      practiceMore: "Practice More: Try to complete at least 2-3 quizzes per day for better learning retention.",
      earnBadges: "Earn Badges: Complete more quizzes and achieve high scores to unlock exciting badges and rewards!"
    },
    
    // Footer
    footer: {
      copyright: "JR Kids Learning App. All rights reserved.",
      madeWith: "Made with",
      by: "by"
    }
  },
  
  bn: {
    // Header
    appTitle: "জেআর কিডস লার্নিং হাব",
    
    // Navigation
    nav: {
      home: "হোম",
      dashboard: "ড্যাশবোর্ড",
      gameZone: "গেম জোন"
    },
    
    // Common
    common: {
      start: "শুরু করুন",
      continue: "চালিয়ে যান",
      back: "ফিরে যান",
      next: "পরবর্তী",
      previous: "পূর্ববর্তী",
      close: "বন্ধ করুন",
      cancel: "বাতিল",
      save: "সংরক্ষণ",
      delete: "মুছুন",
      edit: "সম্পাদনা",
      loading: "লোড হচ্ছে...",
      tryAgain: "আবার চেষ্টা করুন",
      backToHome: "হোমে ফিরে যান",
      awesome: "দুর্দান্ত!",
      congratulations: "অভিনন্দন!"
    },
    
    // Categories
    categories: {
      math: "গণিত",
      science: "বিজ্ঞান",
      geography: "ভূগোল",
      animals: "প্রাণী",
      history: "ইতিহাস",
      space: "মহাকাশ",
      english: "ইংরেজি",
      shapes: "আকৃতি",
      colors: "রং",
      music: "সংগীত",
      sports: "খেলাধুলা",
      food: "খাবার",
      technology: "প্রযুক্তি",
      art: "শিল্পকলা",
      nature: "প্রকৃতি"
    },
    
    // Category Descriptions
    categoryDesc: {
      math: "সংখ্যা, গণনা এবং মৌলিক গণিত",
      science: "বিজ্ঞানের বিস্ময় অন্বেষণ করুন",
      geography: "দেশ এবং স্থান সম্পর্কে জানুন",
      animals: "আশ্চর্যজনক প্রাণী আবিষ্কার করুন",
      history: "সময়ের মধ্য দিয়ে যাত্রা",
      space: "মহাবিশ্ব অন্বেষণ করুন",
      english: "ভাষা এবং ব্যাকরণ",
      shapes: "জ্যামিতিক আকার সম্পর্কে জানুন",
      colors: "রংধনু আবিষ্কার করুন",
      music: "ছন্দ এবং সুর",
      sports: "খেলা এবং অ্যাথলেটিক্স",
      food: "পুষ্টি এবং রান্না",
      technology: "কম্পিউটার এবং গ্যাজেট",
      art: "সৃজনশীলতা এবং প্রকাশ",
      nature: "উদ্ভিদ এবং পরিবেশ"
    },
    
    // Quiz
    quiz: {
      selectCategory: "একটি বিভাগ নির্বাচন করুন",
      question: "প্রশ্ন",
      of: "এর",
      score: "স্কোর",
      correct: "সঠিক",
      yourAnswer: "আপনার উত্তর:",
      correctAnswer: "সঠিক উত্তর:",
      reviewAnswers: "আপনার উত্তর পর্যালোচনা করুন",
      noAnswer: "কোন উত্তর নেই (সময় শেষ)",
      timeUp: "সময় শেষ!",
      quizEnded: "সময় শেষ হওয়ায় কুইজ শেষ হয়েছে",
      completeWithinTime: "XP পেতে সময়ের মধ্যে কুইজ সম্পূর্ণ করুন!"
    },
    
    // Results
    results: {
      perfectScore: "নিখুঁত স্কোর! আপনি একজন প্রতিভাবান! 🎉",
      excellent: "চমৎকার কাজ! এভাবে চালিয়ে যান! 🌟",
      goodJob: "ভালো কাজ! অনুশীলন নিখুঁত করে তোলে! 👍",
      keepTrying: "চেষ্টা চালিয়ে যান! পরের বার আরও ভালো করবেন! 💪"
    },
    
    // Difficulty
    difficulty: {
      easy: "সহজ",
      medium: "মাঝারি",
      hard: "কঠিন",
      selectDifficulty: "অসুবিধা নির্বাচন করুন",
      ages46: "বয়স ৪-৬",
      ages79: "বয়স ৭-৯",
      ages10plus: "বয়স ১০+",
      simpleQuestions: "ছোট শিক্ষার্থীদের জন্য সহজ প্রশ্ন",
      moderateChallenge: "বর্ধনশীল মনের জন্য মাঝারি চ্যালেঞ্জ",
      advancedQuestions: "বিশেষজ্ঞদের জন্য উন্নত প্রশ্ন"
    },
    
    // Quiz Modes
    modes: {
      practice: "অনুশীলন মোড",
      timed: "সময়সীমা মোড",
      practiceDesc: "আপনার সময় নিন, কোন চাপ নেই!",
      timedDesc: "ঘড়ির বিরুদ্ধে দৌড়!",
      selectMode: "কুইজ মোড নির্বাচন করুন"
    },
    
    // Dashboard
    dashboard: {
      title: "শিক্ষা ড্যাশবোর্ড",
      totalQuizzes: "মোট কুইজ",
      averageScore: "গড় স্কোর",
      badgesEarned: "অর্জিত ব্যাজ",
      bestCategory: "সেরা বিভাগ",
      categoryPerformance: "বিভাগ পারফরম্যান্স",
      quizzesCompleted: "সম্পন্ন কুইজ",
      bestScore: "সেরা স্কোর",
      averageScoreLabel: "গড় স্কোর",
      lastPlayed: "শেষ খেলা",
      recentActivity: "সাম্প্রতিক কার্যকলাপ",
      downloadReport: "প্রগতি রিপোর্ট ডাউনলোড করুন"
    },
    
    // Streak
    streak: {
      dayStreak: "দিনের ধারা",
      longestStreak: "দীর্ঘতম ধারা",
      totalDays: "মোট দিন",
      thisMonth: "এই মাসে",
      freezeAvailable: "ফ্রিজ উপলব্ধ",
      streakFreeze: "স্ট্রিক ফ্রিজ: আপনার ধারা ভাঙা ছাড়াই ১ দিন মিস করুন!",
      viewCalendar: "কার্যকলাপ ক্যালেন্ডার দেখুন",
      keepGoing: "আগামীকাল চালিয়ে যান!",
      greatStart: "দুর্দান্ত শুরু! 🎯"
    },
    
    // Gamification
    gamification: {
      level: "লেভেল",
      beginner: "শিক্ষানবিস",
      intermediate: "মধ্যবর্তী",
      advanced: "উন্নত",
      expert: "বিশেষজ্ঞ",
      grandMaster: "গ্র্যান্ড মাস্টার",
      legendaryMaster: "কিংবদন্তি মাস্টার",
      experiencePoints: "অভিজ্ঞতা পয়েন্ট",
      totalXP: "মোট XP",
      xpToLevel: "লেভেল পর্যন্ত XP",
      viewLeaderboard: "লিডারবোর্ড দেখুন",
      dailyChallenges: "দৈনিক চ্যালেঞ্জ",
      complete: "সম্পূর্ণ",
      levelUp: "লেভেল {level}!",
      youveReached: "আপনি {title} পৌঁছেছেন!"
    },
    
    // Game Zone
    gameZone: {
      title: "গেম জোন",
      subtitle: "আপনার অগ্রগতি ট্র্যাক করুন, চ্যালেঞ্জ সম্পূর্ণ করুন এবং লিডারবোর্ডে উঠুন!",
      selectGame: "একটি গেম নির্বাচন করুন",
      memoryMatch: "মেমরি ম্যাচ",
      wordScramble: "ওয়ার্ড স্ক্র্যাম্বল",
      mathPuzzle: "ম্যাথ পাজল",
      memoryDesc: "মিলে যাওয়া জোড়া খুঁজুন!",
      wordDesc: "শব্দগুলো সাজান!",
      mathDesc: "গণিত সমস্যা সমাধান করুন!",
      moves: "চাল",
      time: "সময়",
      youWon: "আপনি জিতেছেন!",
      gameComplete: "গেম সম্পূর্ণ!",
      playNow: "এখনই খেলুন",
      miniGames: "মিনি-গেমস",
      levelProgress: "আপনার লেভেল এবং অগ্রগতি",
      quickStats: "দ্রুত পরিসংখ্যান",
      daysPlayed: "খেলার দিন",
      learningStreak: "শেখার ধারা",
      competition: "প্রতিযোগিতা",
      leaderboard: "লিডারবোর্ড",
      leaderboardDesc: "অন্যান্য খেলোয়াড়দের বিপরীতে আপনার র‍্যাঙ্ক দেখুন!",
      howToEarnXP: "আরও XP অর্জন করার উপায়",
      tip1: "✅ 2x XP এর জন্য কঠিন অসুবিধায় কুইজ সম্পূর্ণ করুন",
      tip2: "⏱️ বোনাস পয়েন্টের জন্য সময়সীমা মোডে খেলুন",
      tip3: "💯 অতিরিক্ত XP এর জন্য নিখুঁত স্কোর পান",
      tip4: "🔥 স্ট্রিক XP এর জন্য আপনার দৈনিক ধারা বজায় রাখুন",
      tip5: "🎯 প্রতিটি 50-100 XP এর জন্য দৈনিক চ্যালেঞ্জ সম্পূর্ণ করুন",
      tip6: "🏆 প্রতিটি 30 XP এর জন্য ব্যাজ অর্জন করুন"
    },
    
    // Profile
    profile: {
      selectProfile: "আপনার প্রোফাইল নির্বাচন করুন",
      createProfile: "নতুন প্রোফাইল তৈরি করুন",
      manageProfiles: "প্রোফাইল পরিচালনা করুন",
      profileName: "প্রোফাইল নাম",
      age: "বয়স",
      selectAvatar: "অবতার নির্বাচন করুন",
      selectColor: "রঙ নির্বাচন করুন",
      editProfile: "প্রোফাইল সম্পাদনা করুন",
      deleteProfile: "প্রোফাইল মুছুন",
      confirmDelete: "আপনি কি নিশ্চিত যে এই প্রোফাইলটি মুছতে চান?",
      logout: "লগআউট"
    },
    
    // Badges
    badges: {
      earnedBadges: "অর্জিত ব্যাজ",
      recommendations: "সুপারিশ",
      focusArea: "ফোকাস এরিয়া: সামগ্রিক পারফরম্যান্স উন্নত করতে {category} এ আরও সময় ব্যয় করার কথা বিবেচনা করুন।",
      practiceMore: "আরও অনুশীলন: ভাল শেখার ধারণক্ষমতার জন্য প্রতিদিন কমপক্ষে ২-৩টি কুইজ সম্পূর্ণ করার চেষ্টা করুন।",
      earnBadges: "ব্যাজ অর্জন করুন: আরও কুইজ সম্পূর্ণ করুন এবং উত্তেজনাপূর্ণ ব্যাজ এবং পুরস্কার আনলক করতে উচ্চ স্কোর অর্জন করুন!"
    },
    
    // Footer
    footer: {
      copyright: "জেআর কিডস লার্নিং অ্যাপ। সর্বস্বত্ব সংরক্ষিত।",
      madeWith: "তৈরি করেছেন",
      by: "দ্বারা"
    }
  },
  
  ar: {
    // Header
    appTitle: "مركز التعلم للأطفال JR",
    
    // Navigation
    nav: {
      home: "الرئيسية",
      dashboard: "لوحة التحكم",
      gameZone: "منطقة الألعاب"
    },
    
    // Common
    common: {
      start: "ابدأ",
      continue: "استمر",
      back: "رجوع",
      next: "التالي",
      previous: "السابق",
      close: "إغلاق",
      cancel: "إلغاء",
      save: "حفظ",
      delete: "حذف",
      edit: "تعديل",
      loading: "جاري التحميل...",
      tryAgain: "حاول مرة أخرى",
      backToHome: "العودة للرئيسية",
      awesome: "رائع!",
      congratulations: "تهانينا!"
    },
    
    // Categories
    categories: {
      math: "الرياضيات",
      science: "العلوم",
      geography: "الجغرافيا",
      animals: "الحيوانات",
      history: "التاريخ",
      space: "الفضاء",
      english: "الإنجليزية",
      shapes: "الأشكال",
      colors: "الألوان",
      music: "الموسيقى",
      sports: "الرياضة",
      food: "الطعام",
      technology: "التكنولوجيا",
      art: "الفن",
      nature: "الطبيعة"
    },
    
    // Category Descriptions
    categoryDesc: {
      math: "الأرقام والعد والحساب الأساسي",
      science: "استكشف عجائب العلوم",
      geography: "تعرف على البلدان والأماكن",
      animals: "اكتشف المخلوقات المدهشة",
      history: "رحلة عبر الزمن",
      space: "استكشف الكون",
      english: "اللغة والقواعد",
      shapes: "تعلم عن الأشكال الهندسية",
      colors: "اكتشف قوس قزح",
      music: "الإيقاع والألحان",
      sports: "الألعاب والرياضة",
      food: "التغذية والطبخ",
      technology: "أجهزة الكمبيوتر والأدوات",
      art: "الإبداع والتعبير",
      nature: "النباتات والبيئة"
    },
    
    // Quiz
    quiz: {
      selectCategory: "اختر فئة",
      question: "سؤال",
      of: "من",
      score: "النتيجة",
      correct: "صحيح",
      yourAnswer: "إجابتك:",
      correctAnswer: "الإجابة الصحيحة:",
      reviewAnswers: "راجع إجاباتك",
      noAnswer: "لا إجابة (انتهى الوقت)",
      timeUp: "انتهى الوقت!",
      quizEnded: "انتهى الاختبار بسبب انتهاء الوقت",
      completeWithinTime: "أكمل الاختبار في الوقت المحدد لكسب XP!"
    },
    
    // Results
    results: {
      perfectScore: "نتيجة مثالية! أنت عبقري! 🎉",
      excellent: "عمل ممتاز! استمر! 🌟",
      goodJob: "عمل جيد! الممارسة تصنع الكمال! 👍",
      keepTrying: "استمر في المحاولة! ستفعل أفضل في المرة القادمة! 💪"
    },
    
    // Difficulty
    difficulty: {
      easy: "سهل",
      medium: "متوسط",
      hard: "صعب",
      selectDifficulty: "اختر المستوى",
      ages46: "الأعمار 4-6",
      ages79: "الأعمار 7-9",
      ages10plus: "الأعمار 10+",
      simpleQuestions: "أسئلة بسيطة للمتعلمين الصغار",
      moderateChallenge: "تحدي معتدل للعقول النامية",
      advancedQuestions: "أسئلة متقدمة للخبراء"
    },
    
    // Quiz Modes
    modes: {
      practice: "وضع التدريب",
      timed: "وضع الوقت",
      practiceDesc: "خذ وقتك، لا ضغط!",
      timedDesc: "سباق مع الزمن!",
      selectMode: "اختر وضع الاختبار"
    },
    
    // Dashboard
    dashboard: {
      title: "لوحة التعلم",
      totalQuizzes: "إجمالي الاختبارات",
      averageScore: "متوسط النتيجة",
      badgesEarned: "الشارات المكتسبة",
      bestCategory: "أفضل فئة",
      categoryPerformance: "أداء الفئة",
      quizzesCompleted: "الاختبارات المكتملة",
      bestScore: "أفضل نتيجة",
      averageScoreLabel: "متوسط النتيجة",
      lastPlayed: "آخر لعب",
      recentActivity: "النشاط الأخير",
      downloadReport: "تحميل تقرير التقدم"
    },
    
    // Streak
    streak: {
      dayStreak: "سلسلة الأيام",
      longestStreak: "أطول سلسلة",
      totalDays: "إجمالي الأيام",
      thisMonth: "هذا الشهر",
      freezeAvailable: "التجميد متاح",
      streakFreeze: "تجميد السلسلة: فوت يوم واحد دون كسر سلسلتك!",
      viewCalendar: "عرض تقويم النشاط",
      keepGoing: "استمر غداً!",
      greatStart: "بداية رائعة! 🎯"
    },
    
    // Gamification
    gamification: {
      level: "المستوى",
      beginner: "مبتدئ",
      intermediate: "متوسط",
      advanced: "متقدم",
      expert: "خبير",
      grandMaster: "السيد الكبير",
      legendaryMaster: "السيد الأسطوري",
      experiencePoints: "نقاط الخبرة",
      totalXP: "إجمالي XP",
      xpToLevel: "XP للمستوى",
      viewLeaderboard: "عرض لوحة المتصدرين",
      dailyChallenges: "التحديات اليومية",
      complete: "مكتمل",
      levelUp: "المستوى {level}!",
      youveReached: "لقد وصلت إلى {title}!"
    },
    
    // Game Zone
    gameZone: {
      title: "منطقة الألعاب",
      subtitle: "تتبع تقدمك، أكمل التحديات، وتسلق لوحة المتصدرين!",
      selectGame: "اختر لعبة",
      memoryMatch: "مطابقة الذاكرة",
      wordScramble: "تشويش الكلمات",
      mathPuzzle: "لغز الرياضيات",
      memoryDesc: "ابحث عن الأزواج المتطابقة!",
      wordDesc: "رتب الكلمات!",
      mathDesc: "حل مسائل الرياضيات!",
      moves: "الحركات",
      time: "الوقت",
      youWon: "لقد فزت!",
      gameComplete: "اكتملت اللعبة!",
      playNow: "العب الآن",
      miniGames: "ألعاب صغيرة",
      levelProgress: "مستواك وتقدمك",
      quickStats: "إحصائيات سريعة",
      daysPlayed: "أيام اللعب",
      learningStreak: "سلسلة التعلم",
      competition: "المنافسة",
      leaderboard: "لوحة المتصدرين",
      leaderboardDesc: "شاهد ترتيبك مقابل اللاعبين الآخرين!",
      howToEarnXP: "كيفية كسب المزيد من XP",
      tip1: "✅ أكمل الاختبارات على صعوبة صعبة للحصول على 2x XP",
      tip2: "⏱️ العب في الوضع الموقوت للحصول على نقاط إضافية",
      tip3: "💯 احصل على درجات مثالية للحصول على XP إضافي",
      tip4: "🔥 حافظ على سلسلتك اليومية للحصول على XP السلسلة",
      tip5: "🎯 أكمل التحديات اليومية للحصول على 50-100 XP لكل منها",
      tip6: "🏆 اكسب الشارات للحصول على 30 XP لكل منها"
    },
    
    // Profile
    profile: {
      selectProfile: "اختر ملفك الشخصي",
      createProfile: "إنشاء ملف شخصي جديد",
      manageProfiles: "إدارة الملفات الشخصية",
      profileName: "اسم الملف الشخصي",
      age: "العمر",
      selectAvatar: "اختر الصورة الرمزية",
      selectColor: "اختر اللون",
      editProfile: "تعديل الملف الشخصي",
      deleteProfile: "حذف الملف الشخصي",
      confirmDelete: "هل أنت متأكد أنك تريد حذف هذا الملف الشخصي؟",
      logout: "تسجيل الخروج"
    },
    
    // Badges
    badges: {
      earnedBadges: "الشارات المكتسبة",
      recommendations: "التوصيات",
      focusArea: "منطقة التركيز: فكر في قضاء المزيد من الوقت في {category} لتحسين الأداء العام.",
      practiceMore: "تدرب أكثر: حاول إكمال 2-3 اختبارات على الأقل يومياً لتحسين الاحتفاظ بالتعلم.",
      earnBadges: "اكسب الشارات: أكمل المزيد من الاختبارات واحصل على درجات عالية لفتح شارات ومكافآت مثيرة!"
    },
    
    // Footer
    footer: {
      copyright: "تطبيق التعلم للأطفال JR. جميع الحقوق محفوظة.",
      madeWith: "صنع بـ",
      by: "بواسطة"
    }
  },
  
  hi: {
    // Header
    appTitle: "JR किड्स लर्निंग हब",
    
    // Navigation
    nav: {
      home: "होम",
      dashboard: "डैशबोर्ड",
      gameZone: "गेम ज़ोन"
    },
    
    // Common
    common: {
      start: "शुरू करें",
      continue: "जारी रखें",
      back: "वापस",
      next: "अगला",
      previous: "पिछला",
      close: "बंद करें",
      cancel: "रद्द करें",
      save: "सहेजें",
      delete: "हटाएं",
      edit: "संपादित करें",
      loading: "लोड हो रहा है...",
      tryAgain: "फिर से कोशिश करें",
      backToHome: "होम पर वापस जाएं",
      awesome: "शानदार!",
      congratulations: "बधाई हो!"
    },
    
    // Categories
    categories: {
      math: "गणित",
      science: "विज्ञान",
      geography: "भूगोल",
      animals: "जानवर",
      history: "इतिहास",
      space: "अंतरिक्ष",
      english: "अंग्रेजी",
      shapes: "आकार",
      colors: "रंग",
      music: "संगीत",
      sports: "खेल",
      food: "भोजन",
      technology: "प्रौद्योगिकी",
      art: "कला",
      nature: "प्रकृति"
    },
    
    // Category Descriptions
    categoryDesc: {
      math: "संख्या, गिनती और बुनियादी अंकगणित",
      science: "विज्ञान के चमत्कारों का अन्वेषण करें",
      geography: "देशों और स्थानों के बारे में जानें",
      animals: "अद्भुत जीवों की खोज करें",
      history: "समय के माध्यम से यात्रा",
      space: "ब्रह्मांड का अन्वेषण करें",
      english: "भाषा और व्याकरण",
      shapes: "ज्यामितीय आकारों के बारे में जानें",
      colors: "इंद्रधनुष की खोज करें",
      music: "लय और धुन",
      sports: "खेल और एथलेटिक्स",
      food: "पोषण और खाना पकाना",
      technology: "कंप्यूटर और गैजेट",
      art: "रचनात्मकता और अभिव्यक्ति",
      nature: "पौधे और पर्यावरण"
    },
    
    // Quiz
    quiz: {
      selectCategory: "एक श्रेणी चुनें",
      question: "प्रश्न",
      of: "का",
      score: "स्कोर",
      correct: "सही",
      yourAnswer: "आपका उत्तर:",
      correctAnswer: "सही उत्तर:",
      reviewAnswers: "अपने उत्तरों की समीक्षा करें",
      noAnswer: "कोई उत्तर नहीं (समय समाप्त)",
      timeUp: "समय समाप्त!",
      quizEnded: "समय समाप्त होने के कारण क्विज़ समाप्त हो गई",
      completeWithinTime: "XP अर्जित करने के लिए समय के भीतर क्विज़ पूरी करें!"
    },
    
    // Results
    results: {
      perfectScore: "परफेक्ट स्कोर! आप एक प्रतिभाशाली हैं! 🎉",
      excellent: "उत्कृष्ट काम! इसे जारी रखें! 🌟",
      goodJob: "अच्छा काम! अभ्यास परिपूर्ण बनाता है! 👍",
      keepTrying: "कोशिश करते रहें! अगली बार आप बेहतर करेंगे! 💪"
    },
    
    // Difficulty
    difficulty: {
      easy: "आसान",
      medium: "मध्यम",
      hard: "कठिन",
      selectDifficulty: "कठिनाई चुनें",
      ages46: "उम्र 4-6",
      ages79: "उम्र 7-9",
      ages10plus: "उम्र 10+",
      simpleQuestions: "युवा शिक्षार्थियों के लिए सरल प्रश्न",
      moderateChallenge: "बढ़ते दिमाग के लिए मध्यम चुनौती",
      advancedQuestions: "विशेषज्ञों के लिए उन्नत प्रश्न"
    },
    
    // Quiz Modes
    modes: {
      practice: "अभ्यास मोड",
      timed: "समयबद्ध मोड",
      practiceDesc: "अपना समय लें, कोई दबाव नहीं!",
      timedDesc: "घड़ी के खिलाफ दौड़!",
      selectMode: "क्विज़ मोड चुनें"
    },
    
    // Dashboard
    dashboard: {
      title: "लर्निंग डैशबोर्ड",
      totalQuizzes: "कुल क्विज़",
      averageScore: "औसत स्कोर",
      badgesEarned: "अर्जित बैज",
      bestCategory: "सर्वश्रेष्ठ श्रेणी",
      categoryPerformance: "श्रेणी प्रदर्शन",
      quizzesCompleted: "पूर्ण क्विज़",
      bestScore: "सर्वश्रेष्ठ स्कोर",
      averageScoreLabel: "औसत स्कोर",
      lastPlayed: "अंतिम बार खेला",
      recentActivity: "हाल की गतिविधि",
      downloadReport: "प्रगति रिपोर्ट डाउनलोड करें"
    },
    
    // Streak
    streak: {
      dayStreak: "दिन की लकीर",
      longestStreak: "सबसे लंबी लकीर",
      totalDays: "कुल दिन",
      thisMonth: "इस महीने",
      freezeAvailable: "फ्रीज उपलब्ध",
      streakFreeze: "स्ट्रीक फ्रीज: अपनी लकीर तोड़े बिना 1 दिन छोड़ें!",
      viewCalendar: "गतिविधि कैलेंडर देखें",
      keepGoing: "कल जारी रखें!",
      greatStart: "शानदार शुरुआत! 🎯"
    },
    
    // Gamification
    gamification: {
      level: "स्तर",
      beginner: "शुरुआती",
      intermediate: "मध्यवर्ती",
      advanced: "उन्नत",
      expert: "विशेषज्ञ",
      grandMaster: "ग्रैंड मास्टर",
      legendaryMaster: "पौराणिक मास्टर",
      experiencePoints: "अनुभव अंक",
      totalXP: "कुल XP",
      xpToLevel: "स्तर तक XP",
      viewLeaderboard: "लीडरबोर्ड देखें",
      dailyChallenges: "दैनिक चुनौतियां",
      complete: "पूर्ण",
      levelUp: "स्तर {level}!",
      youveReached: "आप {title} तक पहुंच गए हैं!"
    },
    
    // Game Zone
    gameZone: {
      title: "गेम ज़ोन",
      subtitle: "अपनी प्रगति ट्रैक करें, चुनौतियां पूरी करें, और लीडरबोर्ड पर चढ़ें!",
      selectGame: "एक गेम चुनें",
      memoryMatch: "मेमोरी मैच",
      wordScramble: "वर्ड स्क्रैम्बल",
      mathPuzzle: "मैथ पज़ल",
      memoryDesc: "मिलान जोड़े खोजें!",
      wordDesc: "शब्दों को सुलझाएं!",
      mathDesc: "गणित की समस्याएं हल करें!",
      moves: "चालें",
      time: "समय",
      youWon: "आप जीत गए!",
      gameComplete: "गेम पूर्ण!",
      playNow: "अभी खेलें",
      miniGames: "मिनी-गेम्स",
      levelProgress: "आपका स्तर और प्रगति",
      quickStats: "त्वरित आंकड़े",
      daysPlayed: "खेले गए दिन",
      learningStreak: "सीखने की लकीर",
      competition: "प्रतियोगिता",
      leaderboard: "लीडरबोर्ड",
      leaderboardDesc: "देखें कि आप अन्य खिलाड़ियों के मुकाबले कहां हैं!",
      howToEarnXP: "अधिक XP कैसे अर्जित करें",
      tip1: "✅ 2x XP के लिए कठिन कठिनाई पर क्विज़ पूरी करें",
      tip2: "⏱️ बोनस अंकों के लिए समयबद्ध मोड में खेलें",
      tip3: "💯 अतिरिक्त XP के लिए परफेक्ट स्कोर प्राप्त करें",
      tip4: "🔥 स्ट्रीक XP के लिए अपनी दैनिक लकीर बनाए रखें",
      tip5: "🎯 प्रत्येक के लिए 50-100 XP के लिए दैनिक चुनौतियां पूरी करें",
      tip6: "🏆 प्रत्येक के लिए 30 XP के लिए बैज अर्जित करें"
    },
    
    // Profile
    profile: {
      selectProfile: "अपनी प्रोफ़ाइल चुनें",
      createProfile: "नई प्रोफ़ाइल बनाएं",
      manageProfiles: "प्रोफ़ाइल प्रबंधित करें",
      profileName: "प्रोफ़ाइल नाम",
      age: "उम्र",
      selectAvatar: "अवतार चुनें",
      selectColor: "रंग चुनें",
      editProfile: "प्रोफ़ाइल संपादित करें",
      deleteProfile: "प्रोफ़ाइल हटाएं",
      confirmDelete: "क्या आप वाकई इस प्रोफ़ाइल को हटाना चाहते हैं?",
      logout: "लॉगआउट"
    },
    
    // Badges
    badges: {
      earnedBadges: "अर्जित बैज",
      recommendations: "सिफारिशें",
      focusArea: "फोकस क्षेत्र: समग्र प्रदर्शन में सुधार के लिए {category} पर अधिक समय बिताने पर विचार करें।",
      practiceMore: "अधिक अभ्यास करें: बेहतर सीखने की अवधारण के लिए प्रतिदिन कम से कम 2-3 क्विज़ पूरी करने का प्रयास करें।",
      earnBadges: "बैज अर्जित करें: अधिक क्विज़ पूरी करें और रोमांचक बैज और पुरस्कार अनलॉक करने के लिए उच्च स्कोर प्राप्त करें!"
    },
    
    // Footer
    footer: {
      copyright: "JR किड्स लर्निंग ऐप। सर्वाधिकार सुरक्षित।",
      madeWith: "के साथ बनाया गया",
      by: "द्वारा"
    }
  }
};
